import { useCallback, useMemo, useRef } from 'react';
import useQuery, { QueryKey } from 'hooks/useQuery';
import { CancelToken } from 'axios';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import {
  OperationSummaryError,
  OperationSummaryParams,
  OperationSummaryResponse
} from 'components/RecordsListView/types';

/** A summary response plus the crop-season selection it was fetched for. */
type TaggedSummaryResponse<T> = OperationSummaryResponse<T> & {
  fetchedForCropSeasonIds?: string[];
};

interface UseOperationSummaryReturn<T> {
  loading: boolean;
  items: T[];
  totalElements: number;
  totalPages: number;
  pageSize: number;
  currentPage: number;
  numberOfElements: number;
  loadItems: () => Promise<void>;
  errors: OperationSummaryError | null;
  /** Crop seasons the data currently held was fetched for; undefined until the first response. */
  loadedCropSeasonIds?: string[];
}

const useOperationSummary = <T,>(
  queryKey: string,
  fetchFn: (
    params?: OperationSummaryParams,
    cancelToken?: CancelToken
  ) => Promise<OperationSummaryResponse<T>>,
  params?: OperationSummaryParams,
  cacheKeyPrefix?: string
): UseOperationSummaryReturn<T> => {
  const firstRenderRef = useRef(true);
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  // Read through a ref inside queryFn so a selection change does not rebuild
  // the query function and trigger a second fetch on top of the consumer's own
  // reload; the tag still records the selection current at fetch time.
  const selectedCropSeasonIdsRef = useRef(selectedCropSeasonIds);
  selectedCropSeasonIdsRef.current = selectedCropSeasonIds;

  const queryFn = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (
      _queryKey: QueryKey,
      cancelToken?: CancelToken
    ): Promise<TaggedSummaryResponse<T>> => {
      // Snapshot before awaiting: this is the selection the fetcher scopes the
      // request to. Read after the await, a selection change made while the
      // request was in flight would retag the previous selection's rows as the
      // new one's, and the latch would accept them.
      const fetchedForCropSeasonIds =
        params?.cropSeasonIds ?? selectedCropSeasonIdsRef.current;
      const response = await fetchFn(params, cancelToken);

      firstRenderRef.current = false;
      // Travels with the data (including through the cache) so consumers can
      // tell whether what they hold answers the current selection, even when
      // it arrived without a loading render. Callers that scope the fetch via
      // params (Invoices) pass the ids explicitly; the operations fetchers read
      // the selection themselves, so fall back to the selection this hook
      // already reads.
      return { ...response, fetchedForCropSeasonIds };
    },
    [fetchFn, params]
  );

  const { data, isLoading, refetch } = useQuery<TaggedSummaryResponse<T>>(
    queryKey,
    queryFn,
    {
      cacheKeyPrefix: cacheKeyPrefix || queryKey,
      cacheTTL: 0, // Disable caching for operation summary
      autoLoadingEnabled: selectedCropSeasonIds.length > 0
    }
  );

  const items = useMemo(() => data?.results || [], [data]);
  const totalElements = useMemo(() => data?.totalElements || 0, [data]);
  const totalPages = useMemo(() => data?.totalPages || 0, [data]);
  const pageSize = useMemo(() => data?.size || 0, [data]);
  const currentPage = useMemo(() => data?.currentPage || 0, [data]);
  const numberOfElements = useMemo(() => data?.numberOfElements || 0, [data]);
  const errors = useMemo(() => data?.errors || null, [data]);

  const loadItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    loading: isLoading,
    items,
    totalElements,
    totalPages,
    pageSize,
    currentPage,
    numberOfElements,
    loadItems,
    errors,
    loadedCropSeasonIds: data?.fetchedForCropSeasonIds
  };
};

export default useOperationSummary;
